/**
 * BleBridge — global singleton component mounted once inside StoreProvider.
 *
 * Listens for `biostream:ble-data` events dispatched by DataLabBridge.java
 * whenever a BLE notification arrives on the NUS TX characteristic.
 *
 * Responsibilities:
 *  - Decode raw hex proto payload via decodeBiostreamPayload()
 *  - Persist TRIAGE_ALERT and MAN_DOWN to the server via commands
 *  - Re-dispatch a structured `biostream:watch-event` for live-triage.tsx
 *  - STATUS_RESPONSE: re-dispatch only (not persisted per protocol)
 *  - MEDIC_INVITE: dispatch biostream:field-event-invite so the Join prompt appears
 *  - WATCH_RELAY: re-dispatch as biostream:watch-event (LoRa → same DOM event path)
 *    and forward outbound watch events over LoRa when loraSessionActive
 */

import { useEffect, useRef } from "react";
import { useCommand } from "./store";
import { useFieldEvent } from "./field-event-context";
import { decodeBiostreamPayload } from "../../shared/src/proto-decode";
import { encodeWatchEventRelay } from "../../shared/src/proto-encode";
import { uuidv7 } from "../../shared/src/ids";

const TRIAGE_LEVEL_MAP: Record<number, string> = {
  1: "T0",
  2: "T4",
  3: "T3",
  4: "T2",
  5: "T1",
};

// Reverse map for encoding: T-string → proto enum value
const TRIAGE_LEVEL_ENCODE: Record<string, number> = {
  T0: 1, T4: 2, T3: 3, T2: 4, T1: 5,
};

// GPS int32 (degrees × 1e7) → float, null-safe
function intToFloat(i: number | undefined | null): number | null {
  return i != null ? i / 1e7 : null;
}

export function BleBridge() {
  const dispatch = useCommand();
  const { fieldEventId, pairings, boardAddress, loraSessionActive } = useFieldEvent();

  // Keep stable refs so the event listener closure always sees current values
  const fieldEventRef = useRef(fieldEventId);
  const pairingsRef = useRef(pairings);
  const boardAddressRef = useRef(boardAddress);
  const loraActiveRef = useRef(loraSessionActive);

  useEffect(() => { fieldEventRef.current = fieldEventId; }, [fieldEventId]);
  useEffect(() => { pairingsRef.current = pairings; }, [pairings]);
  useEffect(() => { boardAddressRef.current = boardAddress; }, [boardAddress]);
  useEffect(() => { loraActiveRef.current = loraSessionActive; }, [loraSessionActive]);

  useEffect(() => {
    const handler = async (e: Event) => {
      const { address: _address, name: _name, hex } =
        (e as CustomEvent<{ address: string; name: string | null; hex: string }>).detail;

      const decoded = decodeBiostreamPayload(hex);
      if (!decoded || decoded.type === "unknown") return;

      const evtId = fieldEventRef.current;
      const pairing = pairingsRef.current.get(decoded.watchId);

      // ── TRIAGE_ALERT ──────────────────────────────────────────────────
      if (decoded.type === "triage_alert") {
        const lat = intToFloat(decoded.latitudeI);
        const lon = intToFloat(decoded.longitudeI);
        const triageLevel = TRIAGE_LEVEL_MAP[decoded.triageLevel ?? 0] ?? null;

        if (evtId) {
          try {
            await dispatch("watchEvent.triageAlert", {
              uuid: uuidv7(),
              fieldEventUuid: evtId,
              watchId: decoded.watchId,
              gpsLat:  lat  ?? undefined,
              gpsLon:  lon  ?? undefined,
              gpsAccuracyM: decoded.gpsAccuracy != null ? decoded.gpsAccuracy / 1000 : undefined,
              payload: {
                triageLevel,
                triageScore:  decoded.triageScore  ?? null,
                confidence:   decoded.confidence   ?? null,
                hrBpm:        decoded.vitals?.hr   ?? null,
                spo2Pct:      decoded.vitals?.spo2 ?? null,
                tempC:        decoded.vitals?.tempC ?? null,
                batteryPct:   decoded.batteryPct   ?? null,
              },
              timestamp: decoded.timestampMs || Date.now(),
            });
          } catch { /* offline — event will sync when back online */ }
        }

        // Forward over LoRa so peer medics see it in real time
        if (loraActiveRef.current && boardAddressRef.current && pairing) {
          (window as any).DataLab?.writeGatt?.(boardAddressRef.current,
            encodeWatchEventRelay(decoded.watchId, {
              type: "TRIAGE_ALERT",
              soldierId:   pairing.soldierId,
              soldierName: pairing.soldierName,
              triageLevel: decoded.triageLevel ?? 0,
              batteryPct:  decoded.batteryPct,
              confidence:  decoded.confidence,
              latitudeI:   decoded.latitudeI,
              longitudeI:  decoded.longitudeI,
            }),
          );
        }

        window.dispatchEvent(new CustomEvent("biostream:watch-event", {
          detail: {
            type: "TRIAGE_ALERT",
            data: {
              watchId:     decoded.watchId,
              soldierId:   pairing?.soldierId   ?? null,
              soldierName: pairing?.soldierName ?? null,
              triageLevel,
              hrBpm:       decoded.vitals?.hr   ?? null,
              spo2Pct:     decoded.vitals?.spo2 ?? null,
              batteryPct:  decoded.batteryPct   ?? null,
              lat, lon,
            },
          },
        }));
      }

      // ── MAN_DOWN ──────────────────────────────────────────────────────
      if (decoded.type === "man_down") {
        const lat = intToFloat(decoded.latitudeI);
        const lon = intToFloat(decoded.longitudeI);

        if (evtId) {
          try {
            await dispatch("watchEvent.manDown", {
              uuid: uuidv7(),
              fieldEventUuid: evtId,
              watchId: decoded.watchId,
              gpsLat:  lat  ?? undefined,
              gpsLon:  lon  ?? undefined,
              gpsAccuracyM: decoded.gpsAccuracy != null ? decoded.gpsAccuracy / 1000 : undefined,
              payload: { confidence: decoded.confidence ?? null },
              timestamp: decoded.timestampMs || Date.now(),
            });
          } catch { /* offline */ }
        }

        // Forward over LoRa
        if (loraActiveRef.current && boardAddressRef.current && pairing) {
          (window as any).DataLab?.writeGatt?.(boardAddressRef.current,
            encodeWatchEventRelay(decoded.watchId, {
              type: "MAN_DOWN",
              soldierId:   pairing.soldierId,
              soldierName: pairing.soldierName,
              triageLevel: 0,
              confidence:  decoded.confidence,
              latitudeI:   decoded.latitudeI,
              longitudeI:  decoded.longitudeI,
            }),
          );
        }

        window.dispatchEvent(new CustomEvent("biostream:watch-event", {
          detail: {
            type: "MAN_DOWN",
            data: {
              watchId:     decoded.watchId,
              soldierId:   pairing?.soldierId   ?? null,
              soldierName: pairing?.soldierName ?? null,
              lat, lon,
            },
          },
        }));
      }

      // ── STATUS_RESPONSE — forward to server (for viewer WS) + local DOM event ──
      if (decoded.type === "status_response") {
        const lat = intToFloat(decoded.latitudeI);
        const lon = intToFloat(decoded.longitudeI);

        // Submit to server so viewers receive it via WS (noOp projection — not persisted)
        if (evtId) {
          try {
            await dispatch("watchEvent.statusResponse", {
              uuid: uuidv7(),
              fieldEventUuid: evtId,
              watchId: decoded.watchId,
              soldierUuid: pairing?.soldierId,
              hrBpm:      decoded.vitals?.hr   ?? null,
              spo2Pct:    decoded.vitals?.spo2 ?? null,
              batteryPct: decoded.batteryPct   ?? null,
              gpsLat: lat ?? undefined,
              gpsLon: lon ?? undefined,
              gpsAccuracyM: decoded.gpsAccuracy != null ? decoded.gpsAccuracy / 1000 : undefined,
              timestamp: decoded.timestampMs || Date.now(),
            });
          } catch { /* offline */ }
        }

        // Update CoT marker here in BleBridge — live-triage may not be mounted yet
        if ((window as any).BIOSTREAM_ENV?.atak && lat != null && lon != null && pairing) {
          (window as any).DataLab?.addCotMarker?.(
            decoded.watchId, pairing.soldierName, lat, lon,
            `HR ${decoded.vitals?.hr ?? "—"} · SpO₂ ${decoded.vitals?.spo2 ?? "—"}%`,
            "",
          );
        }

        window.dispatchEvent(new CustomEvent("biostream:watch-event", {
          detail: {
            type: "STATUS_RESPONSE",
            data: {
              watchId:    decoded.watchId,
              soldierId:  pairing?.soldierId  ?? null,
              hrBpm:      decoded.vitals?.hr   ?? null,
              spo2Pct:    decoded.vitals?.spo2 ?? null,
              batteryPct: decoded.batteryPct   ?? null,
              lat, lon,
            },
          },
        }));
      }

      // ── MEDIC_INVITE — peer medic is inviting us into their event ─────
      if (decoded.type === "medic_invite" && decoded.fieldEventId) {
        window.dispatchEvent(new CustomEvent("biostream:field-event-invite", {
          detail: {
            fieldEventId:  decoded.fieldEventId,
            invitedByName: decoded.hostName ?? "Unknown",
          },
        }));
      }

      // ── WATCH_RELAY — relayed watch event from host medic over LoRa ───
      if (decoded.type === "watch_relay" && decoded.relayType) {
        const lat = intToFloat(decoded.latitudeI);
        const lon = intToFloat(decoded.longitudeI);
        window.dispatchEvent(new CustomEvent("biostream:watch-event", {
          detail: {
            type: decoded.relayType,
            data: {
              watchId:     decoded.watchId,
              soldierId:   decoded.relaySoldierId   ?? null,
              soldierName: decoded.relaySoldierName ?? null,
              triageLevel: decoded.triageLevel != null
                ? (TRIAGE_LEVEL_MAP[decoded.triageLevel] ?? null)
                : null,
              hrBpm:       decoded.vitals?.hr   ?? null,
              spo2Pct:     decoded.vitals?.spo2 ?? null,
              batteryPct:  decoded.batteryPct   ?? null,
              confidence:  decoded.confidence   ?? null,
              lat, lon,
            },
          },
        }));
      }
    };

    window.addEventListener("biostream:ble-data", handler);
    return () => window.removeEventListener("biostream:ble-data", handler);
  }, [dispatch]);

  return null;
}
